import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Table,
  Tag,
  Button,
  Tabs,
  Statistic,
  Typography,
  Space,
  Spin,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Popconfirm,
  Tooltip,
  DatePicker,
  Descriptions,
  message,
  Switch,
} from "antd";
import {
  DollarOutlined,
  CreditCardOutlined,
  UserOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  StopOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  RollbackOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useList, useCreate, useUpdate, useDelete } from "@refinedev/core";
import { apiService } from "@/services/api";
import type {
  PaymentTransaction,
  PaymentSubscription,
  Plan,
  PaymentProviderConfig,
  PaymentStatistics,
} from "@/types";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export const SubscriptionsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState("transactions");

  // Stats
  const [stats, setStats] = useState<PaymentStatistics | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Transactions state
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionFilters, setTransactionFilters] = useState<any>({});
  const [detailModal, setDetailModal] = useState<{ open: boolean; data: any }>({
    open: false,
    data: null,
  });

  // Subscriptions state
  const [subscriptions, setSubscriptions] = useState<PaymentSubscription[]>([]);
  const [subscriptionsTotal, setSubscriptionsTotal] = useState(0);
  const [subscriptionsPage, setSubscriptionsPage] = useState(1);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState<string | undefined>();

  // Grant subscription (admin manual)
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantForm] = Form.useForm();

  // Plans (via Refine)
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [planForm] = Form.useForm();

  const {
    data: plansData,
    isLoading: plansLoading,
    refetch: refetchPlans,
  } = useList<Plan>({ resource: "plans" });

  const { mutate: createPlan, isLoading: creatingPlan } = useCreate();
  const { mutate: updatePlan, isLoading: updatingPlan } = useUpdate();
  const { mutate: deletePlan } = useDelete();

  // Providers state
  const [providers, setProviders] = useState<PaymentProviderConfig[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<PaymentProviderConfig | null>(null);
  const [providerForm] = Form.useForm();
  const [providerCountryFilter, setProviderCountryFilter] = useState<string | undefined>();
  const [providerSearch, setProviderSearch] = useState("");

  // Load stats
  useEffect(() => {
    setStatsLoading(true);
    apiService
      .getPaymentStatistics()
      .then((data) => setStats(data))
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, []);

  // Load transactions
  const loadTransactions = useCallback(
    async (page = 1, filters = transactionFilters) => {
      setTransactionsLoading(true);
      try {
        const res = await apiService.getPaymentTransactions(filters, page, 20);
        setTransactions(res?.payments || res?.data || res?.content || []);
        setTransactionsTotal(res?.totalElements || res?.total || 0);
      } catch {
        setTransactions([]);
      } finally {
        setTransactionsLoading(false);
      }
    },
    [transactionFilters]
  );

  // Load subscriptions
  const loadSubscriptions = useCallback(
    async (page = 1, status?: string) => {
      setSubscriptionsLoading(true);
      try {
        const res = await apiService.getAdminSubscriptions(page, 20, status);
        setSubscriptions(res?.subscriptions || res?.data || res?.content || []);
        setSubscriptionsTotal(res?.totalElements || res?.total || 0);
      } catch {
        setSubscriptions([]);
      } finally {
        setSubscriptionsLoading(false);
      }
    },
    []
  );

  // Load providers
  const loadProviders = useCallback(async () => {
    setProvidersLoading(true);
    try {
      const res = await apiService.getPaymentProviders();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const list = Array.isArray(res) ? res : (res as any)?.providers || [];
      setProviders(list);
    } catch (err) {
      console.error("Erreur chargement fournisseurs:", err);
      message.error("Impossible de charger les fournisseurs");
      setProviders([]);
    } finally {
      setProvidersLoading(false);
    }
  }, []);

  // Filtered providers
  const filteredProviders = providers.filter((p) => {
    if (providerCountryFilter && p.countryCode !== providerCountryFilter) return false;
    if (providerSearch) {
      const q = providerSearch.toLowerCase();
      return (
        (p.paymentProvider || "").toLowerCase().includes(q) ||
        // Sans cela, chercher « wave » ne remontait rien : le nom du
        // portefeuille n'etait pas dans le champ de recherche.
        (p.walletCode || "").toLowerCase().includes(q) ||
        (p.displayName || "").toLowerCase().includes(q) ||
        (p.countryName || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Unique countries from providers
  const providerCountries = Array.from(
    new Map(providers.map((p) => [p.countryCode, p.countryName])).entries()
  ).map(([code, name]) => ({ value: code, label: name || code }));

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === "transactions") loadTransactions(1);
    if (activeTab === "subscriptions") loadSubscriptions(1, subscriptionStatusFilter);
    if (activeTab === "providers") loadProviders();
  }, [activeTab]);

  // === Handlers ===
  const handleRefund = async (id: number) => {
    try {
      await apiService.refundPayment(id);
      message.success("Remboursement effectué");
      loadTransactions(transactionsPage);
    } catch {
      message.error("Erreur lors du remboursement");
    }
  };

  const handleViewTransaction = async (id: number) => {
    try {
      const detail = await apiService.getPaymentTransactionDetails(id);
      setDetailModal({ open: true, data: detail });
    } catch {
      message.error("Impossible de charger les détails");
    }
  };

  const handleCancelSubscription = async (id: number) => {
    try {
      await apiService.cancelSubscription(id);
      message.success("Abonnement annulé");
      loadSubscriptions(subscriptionsPage, subscriptionStatusFilter);
    } catch {
      message.error("Erreur lors de l'annulation");
    }
  };

  const handleGrantSubscription = async () => {
    try {
      const values = await grantForm.validateFields();
      setGrantLoading(true);
      await apiService.grantSubscription(values);
      message.success(`Abonnement accordé à ${values.userId}`);
      setGrantModalOpen(false);
      grantForm.resetFields();
      loadSubscriptions(1, subscriptionStatusFilter);
    } catch (err: any) {
      if (err?.errorFields) return;
      const msg = err?.response?.data?.error || err?.message || "Erreur";
      message.error("Erreur : " + msg);
    } finally {
      setGrantLoading(false);
    }
  };

  const handlePlanSubmit = async () => {
    try {
      const values = await planForm.validateFields();
      if (editingPlan) {
        updatePlan(
          { resource: "plans", id: editingPlan.id, values },
          {
            onSuccess: () => {
              message.success("Plan modifié");
              setPlanModalOpen(false);
              setEditingPlan(null);
              planForm.resetFields();
              refetchPlans();
            },
            onError: () => message.error("Erreur lors de la modification"),
          }
        );
      } else {
        createPlan(
          { resource: "plans", values },
          {
            onSuccess: () => {
              message.success("Plan créé");
              setPlanModalOpen(false);
              planForm.resetFields();
              refetchPlans();
            },
            onError: () => message.error("Erreur lors de la création"),
          }
        );
      }
    } catch {
      // validation error
    }
  };

  const handleDeletePlan = (id: number) => {
    deletePlan(
      { resource: "plans", id },
      {
        onSuccess: () => {
          message.success("Plan supprimé");
          refetchPlans();
        },
        onError: () => message.error("Erreur lors de la suppression"),
      }
    );
  };

  const handleToggleProvider = async (id: number) => {
    try {
      await apiService.togglePaymentProvider(id);
      message.success("Fournisseur mis à jour");
      loadProviders();
    } catch {
      message.error("Erreur lors de la mise à jour");
    }
  };

  const handleProviderSubmit = async () => {
    try {
      const values = await providerForm.validateFields();
      if (editingProvider) {
        await apiService.updatePaymentProvider(editingProvider.id, values);
        message.success("Fournisseur modifié");
      } else {
        await apiService.createPaymentProvider(values);
        message.success("Fournisseur créé");
      }
      setProviderModalOpen(false);
      setEditingProvider(null);
      providerForm.resetFields();
      loadProviders();
    } catch (err: any) {
      if (err?.errorFields) return; // form validation error
      const msg = err?.response?.data?.error || err?.message || "Erreur";
      message.error(editingProvider ? "Erreur: " + msg : "Erreur: " + msg);
    }
  };

  const handleDeleteProvider = async (id: number) => {
    try {
      await apiService.deletePaymentProvider(id);
      message.success("Fournisseur supprimé");
      loadProviders();
    } catch {
      message.error("Erreur lors de la suppression");
    }
  };

  // === Status helpers ===
  const getPaymentStatusColor = (status: string) => {
    const map: Record<string, string> = {
      SUCCESS: "green",
      FAILED: "red",
      PENDING: "orange",
      CANCELLED: "default",
    };
    return map[status] || "default";
  };

  const getSubscriptionStatusColor = (status: string) => {
    const map: Record<string, string> = {
      ACTIVE: "green",
      TRIALING: "blue",
      PAST_DUE: "orange",
      CANCELED: "red",
      PAUSED: "purple",
      EXPIRED: "default",
      PENDING: "gold",
    };
    return map[status] || "default";
  };

  // === Columns ===
  const transactionColumns = [
    {
      title: "ID Transaction",
      dataIndex: "transactionId",
      key: "transactionId",
      render: (val: string) => <Text copyable={{ text: val }}>{val ? val.substring(0, 12) + "..." : "-"}</Text>,
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      render: (val: number, record: any) => (
        <Text strong>
          {val?.toLocaleString("fr-FR", { style: "currency", currency: record.currency || "XOF" })}
        </Text>
      ),
    },
    {
      title: "Fournisseur",
      dataIndex: "paymentProvider",
      key: "paymentProvider",
      render: (val: string) => <Tag>{val || "-"}</Tag>,
    },
    {
      title: "Méthode",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status: string) => <Tag color={getPaymentStatusColor(status)}>{status}</Tag>,
    },
    {
      title: "Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (val: string) => (val ? new Date(val).toLocaleDateString("fr-FR") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Space>
          <Tooltip title="Voir détails">
            <Button type="text" icon={<EyeOutlined />} onClick={() => handleViewTransaction(record.id)} />
          </Tooltip>
          {record.status === "SUCCESS" && (
            <Popconfirm
              title="Rembourser cette transaction ?"
              onConfirm={() => handleRefund(record.id)}
              okText="Oui"
              cancelText="Non"
            >
              <Tooltip title="Rembourser">
                <Button type="text" icon={<RollbackOutlined />} danger />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const subscriptionColumns = [
    {
      title: "Utilisateur",
      dataIndex: "userId",
      key: "userId",
      render: (val: string) => <Text>{val || "-"}</Text>,
    },
    {
      title: "Plan",
      dataIndex: "planId",
      key: "planId",
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status: string) => <Tag color={getSubscriptionStatusColor(status)}>{status}</Tag>,
    },
    {
      title: "Fournisseur",
      dataIndex: "paymentProvider",
      key: "paymentProvider",
      render: (val: string) => <Tag>{val || "-"}</Tag>,
    },
    {
      title: "Début",
      dataIndex: "startDate",
      key: "startDate",
      render: (val: string) => (val ? new Date(val).toLocaleDateString("fr-FR") : "-"),
    },
    {
      title: "Fin",
      dataIndex: "endDate",
      key: "endDate",
      render: (val: string) => (val ? new Date(val).toLocaleDateString("fr-FR") : "-"),
    },
    {
      title: "Renouvellement",
      dataIndex: "autoRenew",
      key: "autoRenew",
      render: (val: boolean) => (val ? <Tag color="green">Automatique</Tag> : <Tag>Manuel</Tag>),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) =>
        record.status === "ACTIVE" || record.status === "TRIALING" ? (
          <Popconfirm
            title="Annuler cet abonnement ?"
            onConfirm={() => handleCancelSubscription(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Tooltip title="Annuler">
              <Button type="text" icon={<StopOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        ) : null,
    },
  ];

  const handleTogglePlanActive = (record: Plan) => {
    updatePlan(
      { resource: "plans", id: record.id, values: { ...record, isActive: !record.isActive } },
      {
        onSuccess: () => {
          message.success(`Plan ${record.isActive ? "désactivé" : "activé"}`);
          refetchPlans();
        },
        onError: () => message.error("Erreur lors de la mise à jour"),
      }
    );
  };

  const planColumns = [
    { title: "Nom", dataIndex: "name", key: "name", render: (val: string) => <Text strong>{val}</Text> },
    { title: "Identifiant", dataIndex: "planId", key: "planId", render: (val: string) => <Tag>{val}</Tag> },
    {
      title: "Prix XOF",
      dataIndex: "priceXof",
      key: "priceXof",
      render: (val: number) =>
        val != null ? <Text strong style={{ color: "#52c41a" }}>{val.toLocaleString("fr-FR")} FCFA</Text> : "-",
    },
    {
      title: "Prix EUR",
      dataIndex: "priceEur",
      key: "priceEur",
      render: (val: number) =>
        val != null ? <Text strong style={{ color: "#1890ff" }}>{val.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} \u20ac</Text> : "-",
    },
    {
      title: "Prix USD",
      dataIndex: "priceUsd",
      key: "priceUsd",
      render: (val: number) =>
        val != null ? <Text strong style={{ color: "#722ed1" }}>${val.toLocaleString("fr-FR", { minimumFractionDigits: 2 })}</Text> : "-",
    },
    {
      title: "Durée",
      dataIndex: "durationInDays",
      key: "durationInDays",
      render: (val: number | undefined, record: Plan) => {
        if (val) return <Tag color="cyan">{val} jour{val > 1 ? "s" : ""}</Tag>;
        if (record.durationInMonths) return <Tag color="purple">{record.durationInMonths} mois</Tag>;
        return "-";
      },
    },
    {
      title: "Actif",
      dataIndex: "isActive",
      key: "isActive",
      render: (val: boolean, record: Plan) => (
        <Switch
          checked={val !== false}
          checkedChildren="Oui"
          unCheckedChildren="Non"
          onChange={() => handleTogglePlanActive(record)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: Plan) => (
        <Space>
          <Tooltip title="Modifier">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingPlan(record);
                planForm.setFieldsValue(record);
                setPlanModalOpen(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Supprimer ce plan ?"
            onConfirm={() => handleDeletePlan(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Tooltip title="Supprimer">
              <Button type="text" icon={<DeleteOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const providerColumns = [
    {
      title: "Pays",
      dataIndex: "countryName",
      key: "countryName",
      render: (val: string, record: PaymentProviderConfig) => (
        <span>{val} <Tag>{record.countryCode}</Tag></span>
      ),
    },
    {
      // Le portefeuille d'abord, l'intermediaire ensuite : un agregateur
      // dessert plusieurs moyens de paiement, et afficher son nom en tete
      // rendait ses lignes indiscernables les unes des autres.
      title: "Moyen de paiement",
      dataIndex: "walletCode",
      key: "walletCode",
      render: (wallet: string | undefined, record: PaymentProviderConfig) => (
        <span>
          <Text strong>{wallet || record.displayName || record.paymentProvider}</Text>
          {wallet && (
            <Tag color="purple" style={{ marginLeft: 8 }}>
              via {record.displayName || record.paymentProvider}
            </Tag>
          )}
          {record.paymentType && (
            <Tag color={record.paymentType === "mobile_money" ? "green" : record.paymentType === "aggregator" ? "orange" : "blue"} style={{ marginLeft: 8 }}>
              {record.paymentType}
            </Tag>
          )}
        </span>
      ),
    },
    { title: "Devise", dataIndex: "currency", key: "currency", width: 80 },
    { title: "Préfixe", dataIndex: "phonePrefix", key: "phonePrefix", width: 80 },
    {
      title: "Priorité",
      dataIndex: "priority",
      key: "priority",
      width: 80,
      render: (val: number) => <Tag color={val === 1 ? "green" : val === 2 ? "orange" : "default"}>{val}</Tag>,
    },
    {
      title: "Montant",
      key: "amounts",
      render: (_: any, record: PaymentProviderConfig) =>
        record.minAmount != null || record.maxAmount != null ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.minAmount?.toLocaleString("fr-FR") || "0"} - {record.maxAmount?.toLocaleString("fr-FR") || "..."} {record.currency}
          </Text>
        ) : "-",
    },
    {
      title: "Frais",
      key: "fees",
      width: 100,
      render: (_: any, record: PaymentProviderConfig) => {
        const parts = [];
        if (record.transactionFeePercent != null) parts.push(`${record.transactionFeePercent}%`);
        if (record.transactionFeeFixed != null && record.transactionFeeFixed > 0) parts.push(`+${record.transactionFeeFixed}`);
        return parts.length > 0 ? <Text type="secondary">{parts.join(" ")}</Text> : "-";
      },
    },
    {
      title: "Config",
      dataIndex: "config",
      key: "config",
      width: 100,
      render: (val: string) => val ? <Tag color="green">Configuré</Tag> : <Tag color="red">Non configuré</Tag>,
    },
    {
      title: "Actif",
      dataIndex: "isActive",
      key: "isActive",
      width: 80,
      render: (val: boolean, record: any) => (
        <Switch checked={val} onChange={() => handleToggleProvider(record.id)} />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, record: PaymentProviderConfig) => (
        <Space>
          <Tooltip title="Modifier">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingProvider(record);
                providerForm.setFieldsValue({
                  ...record,
                  transactionFeePercent: record.transactionFeePercent,
                  transactionFeeFixed: record.transactionFeeFixed,
                  config: record.config,
                });
                setProviderModalOpen(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Supprimer ce fournisseur ?"
            onConfirm={() => handleDeleteProvider(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Tooltip title="Supprimer">
              <Button type="text" icon={<DeleteOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={2}>Abonnements & Paiements</Title>
      <Text type="secondary" style={{ marginBottom: 24, display: "block" }}>
        Gérez les transactions, abonnements, plans et fournisseurs de paiement
      </Text>

      {/* Stats Cards */}
      <Spin spinning={statsLoading}>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Revenu total"
                value={stats?.totalAmount || 0}
                prefix={<DollarOutlined style={{ color: "#52c41a" }} />}
                suffix="XOF"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Transactions"
                value={stats?.totalPayments || 0}
                prefix={<CreditCardOutlined style={{ color: "#1890ff" }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Taux de réussite"
                value={stats?.successRate || 0}
                prefix={<CheckCircleOutlined style={{ color: "#722ed1" }} />}
                suffix="%"
                precision={1}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Abonnements actifs"
                value={stats?.byStatus?.ACTIVE || 0}
                prefix={<UserOutlined style={{ color: "#faad14" }} />}
              />
            </Card>
          </Col>
        </Row>
      </Spin>

      {/* Tabs */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "transactions",
              label: "Transactions",
              children: (
                <div>
                  <Space style={{ marginBottom: 16 }} wrap>
                    <Select
                      placeholder="Statut"
                      allowClear
                      style={{ width: 140 }}
                      onChange={(val) => {
                        const f = { ...transactionFilters, status: val };
                        setTransactionFilters(f);
                        loadTransactions(1, f);
                      }}
                      options={[
                        { label: "Succès", value: "SUCCESS" },
                        { label: "Échoué", value: "FAILED" },
                        { label: "En attente", value: "PENDING" },
                        { label: "Annulé", value: "CANCELLED" },
                      ]}
                    />
                    <Select
                      placeholder="Fournisseur"
                      allowClear
                      style={{ width: 160 }}
                      onChange={(val) => {
                        const f = { ...transactionFilters, provider: val };
                        setTransactionFilters(f);
                        loadTransactions(1, f);
                      }}
                      options={providers.map((p) => ({
                        label: p.paymentProvider,
                        value: p.paymentProvider,
                      }))}
                    />
                    <Tooltip title="Actualiser">
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={() => loadTransactions(transactionsPage)}
                      />
                    </Tooltip>
                  </Space>
                  <Table
                    columns={transactionColumns}
                    dataSource={transactions}
                    loading={transactionsLoading}
                    rowKey="id"
                    pagination={{
                      current: transactionsPage,
                      total: transactionsTotal,
                      pageSize: 20,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} sur ${total} transactions`,
                      onChange: (page) => {
                        setTransactionsPage(page);
                        loadTransactions(page);
                      },
                    }}
                  />
                </div>
              ),
            },
            {
              key: "subscriptions",
              label: "Abonnements",
              children: (
                <div>
                  <Space style={{ marginBottom: 16 }}>
                    <Select
                      placeholder="Statut"
                      allowClear
                      style={{ width: 160 }}
                      onChange={(val) => {
                        setSubscriptionStatusFilter(val);
                        loadSubscriptions(1, val);
                      }}
                      options={[
                        { label: "Actif", value: "ACTIVE" },
                        { label: "Essai", value: "TRIALING" },
                        { label: "En retard", value: "PAST_DUE" },
                        { label: "Annulé", value: "CANCELED" },
                        { label: "Suspendu", value: "PAUSED" },
                        { label: "Expiré", value: "EXPIRED" },
                        { label: "En attente", value: "PENDING" },
                      ]}
                    />
                    <Tooltip title="Actualiser">
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={() => loadSubscriptions(subscriptionsPage, subscriptionStatusFilter)}
                      />
                    </Tooltip>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        grantForm.resetFields();
                        setGrantModalOpen(true);
                      }}
                    >
                      Accorder un abonnement
                    </Button>
                  </Space>
                  <Table
                    columns={subscriptionColumns}
                    dataSource={subscriptions}
                    loading={subscriptionsLoading}
                    rowKey="id"
                    pagination={{
                      current: subscriptionsPage,
                      total: subscriptionsTotal,
                      pageSize: 20,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} sur ${total} abonnements`,
                      onChange: (page) => {
                        setSubscriptionsPage(page);
                        loadSubscriptions(page, subscriptionStatusFilter);
                      },
                    }}
                  />
                </div>
              ),
            },
            {
              key: "plans",
              label: "Plans",
              children: (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setEditingPlan(null);
                        planForm.resetFields();
                        setPlanModalOpen(true);
                      }}
                    >
                      Nouveau plan
                    </Button>
                  </div>
                  <Table
                    columns={planColumns}
                    dataSource={plansData?.data || []}
                    loading={plansLoading}
                    rowKey="id"
                    pagination={false}
                  />
                </div>
              ),
            },
            {
              key: "providers",
              label: `Fournisseurs (${providers.length})`,
              children: (
                <div>
                  <Space style={{ marginBottom: 16 }} wrap>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setEditingProvider(null);
                        providerForm.resetFields();
                        providerForm.setFieldsValue({ priority: 1, isActive: true, expirationMinutes: 15 });
                        setProviderModalOpen(true);
                      }}
                    >
                      Nouveau fournisseur
                    </Button>
                    <Select
                      placeholder="Filtrer par pays"
                      allowClear
                      style={{ width: 180 }}
                      value={providerCountryFilter}
                      onChange={setProviderCountryFilter}
                      options={providerCountries}
                      showSearch
                      optionFilterProp="label"
                    />
                    <Input
                      placeholder="Rechercher..."
                      prefix={<SearchOutlined />}
                      style={{ width: 200 }}
                      value={providerSearch}
                      onChange={(e) => setProviderSearch(e.target.value)}
                      allowClear
                    />
                    <Tooltip title="Actualiser">
                      <Button icon={<ReloadOutlined />} onClick={loadProviders} />
                    </Tooltip>
                  </Space>
                  <Table
                    columns={providerColumns}
                    dataSource={filteredProviders}
                    loading={providersLoading}
                    rowKey="id"
                    pagination={filteredProviders.length > 20 ? { pageSize: 20 } : false}
                    size="small"
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Transaction Detail Modal */}
      <Modal
        title="Détails de la transaction"
        open={detailModal.open}
        onCancel={() => setDetailModal({ open: false, data: null })}
        footer={null}
        width={600}
      >
        {detailModal.data && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID Transaction">
              {detailModal.data.transactionId}
            </Descriptions.Item>
            <Descriptions.Item label="Montant">
              {detailModal.data.amount?.toLocaleString("fr-FR")} {detailModal.data.currency}
            </Descriptions.Item>
            <Descriptions.Item label="Statut">
              <Tag color={getPaymentStatusColor(detailModal.data.status)}>
                {detailModal.data.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Fournisseur">
              {detailModal.data.paymentProvider}
            </Descriptions.Item>
            <Descriptions.Item label="Méthode">
              {detailModal.data.paymentMethod}
            </Descriptions.Item>
            <Descriptions.Item label="Utilisateur">
              {detailModal.data.userId}
            </Descriptions.Item>
            <Descriptions.Item label="Date">
              {detailModal.data.paymentDate
                ? new Date(detailModal.data.paymentDate).toLocaleString("fr-FR")
                : "-"}
            </Descriptions.Item>
            {detailModal.data.description && (
              <Descriptions.Item label="Description">
                {detailModal.data.description}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* Plan Create/Edit Modal */}
      <Modal
        title={editingPlan ? "Modifier le plan" : "Nouveau plan"}
        open={planModalOpen}
        onCancel={() => {
          setPlanModalOpen(false);
          setEditingPlan(null);
          planForm.resetFields();
        }}
        onOk={handlePlanSubmit}
        confirmLoading={creatingPlan || updatingPlan}
        okText={editingPlan ? "Modifier" : "Créer"}
        cancelText="Annuler"
      >
        <Form form={planForm} layout="vertical">
          <Form.Item
            name="name"
            label="Nom du plan"
            rules={[{ required: true, message: "Le nom est requis" }]}
          >
            <Input placeholder="Ex: Premium" />
          </Form.Item>
          <Form.Item name="planId" label="Identifiant unique">
            <Input placeholder="Ex: premium-monthly" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Description du plan..." />
          </Form.Item>
          <Text strong style={{ display: "block", marginBottom: 8 }}>Tarification multi-devises</Text>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="priceXof"
                label="Prix XOF (FCFA)"
                rules={[{ required: true, message: "Requis" }]}
              >
                <InputNumber style={{ width: "100%" }} min={0} placeholder="1000" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="priceEur"
                label="Prix EUR"
                rules={[{ required: true, message: "Requis" }]}
              >
                <InputNumber style={{ width: "100%" }} min={0} step={0.01} precision={2} placeholder="1.52" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="priceUsd"
                label="Prix USD"
                rules={[{ required: true, message: "Requis" }]}
              >
                <InputNumber style={{ width: "100%" }} min={0} step={0.01} precision={2} placeholder="1.65" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="durationInDays" label="Durée (jours)">
                <InputNumber style={{ width: "100%" }} min={1} placeholder="Ex: 30" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="durationInMonths" label="Durée (mois)">
                <InputNumber style={{ width: "100%" }} min={1} placeholder="Ex: 1" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="isActive" label="Actif" valuePropName="checked" initialValue={true}>
                <Switch checkedChildren="Oui" unCheckedChildren="Non" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Provider Create/Edit Modal */}
      <Modal
        title={editingProvider ? "Modifier le fournisseur" : "Nouveau fournisseur"}
        open={providerModalOpen}
        onCancel={() => {
          setProviderModalOpen(false);
          setEditingProvider(null);
          providerForm.resetFields();
        }}
        onOk={handleProviderSubmit}
        okText={editingProvider ? "Modifier" : "Créer"}
        cancelText="Annuler"
        width={640}
      >
        <Form form={providerForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="countryCode"
                label="Code pays (ISO)"
                rules={[{ required: true, message: "Requis" }]}
              >
                <Input placeholder="Ex: SN, CI, ML" disabled={!!editingProvider} style={{ textTransform: "uppercase" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="countryName" label="Nom du pays" rules={[{ required: true, message: "Requis" }]}>
                <Input placeholder="Ex: Sénégal" disabled={!!editingProvider} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="paymentProvider"
            label="Fournisseur"
            rules={[{ required: true, message: "Requis" }]}
          >
            <Select
              placeholder="Sélectionner un fournisseur"
              disabled={!!editingProvider}
              showSearch
              options={[
                { label: "Orange Money Direct", value: "ORANGE_MONEY_DIRECT" },
                { label: "MTN Mobile Money Direct", value: "MTN_MONEY_DIRECT" },
                { label: "Moov Money Direct", value: "MOOV_MONEY_DIRECT" },
                { label: "Wave Direct", value: "WAVE_DIRECT" },
                { label: "Free Money Direct", value: "FREE_MONEY_DIRECT" },
                { label: "Airtel Money Direct", value: "AIRTEL_MONEY_DIRECT" },
                { label: "Intouch (Agrégateur)", value: "INTOUCH" },
                { label: "CinetPay (Agrégateur)", value: "CINETPAY" },
                { label: "PayDunya (Agrégateur)", value: "PAYDUNYA" },
                { label: "Flutterwave (Agrégateur)", value: "FLUTTERWAVE" },
                { label: "Paystack (Agrégateur)", value: "PAYSTACK" },
                { label: "Stripe (Cartes)", value: "STRIPE" },
                { label: "PayPal", value: "PAYPAL" },
              ]}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="priority" label="Priorité" rules={[{ required: true, message: "Requis" }]}>
                <InputNumber style={{ width: "100%" }} min={1} max={10} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currency" label="Devise">
                <Select
                  placeholder="Devise"
                  allowClear
                  options={[
                    { label: "XOF (FCFA)", value: "XOF" },
                    { label: "XAF (FCFA)", value: "XAF" },
                    { label: "GHS (Cedi)", value: "GHS" },
                    { label: "EUR", value: "EUR" },
                    { label: "USD", value: "USD" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="phonePrefix" label="Préfixe tél.">
                <Input placeholder="Ex: +221" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="minAmount" label="Montant min">
                <InputNumber style={{ width: "100%" }} min={0} placeholder="500" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="maxAmount" label="Montant max">
                <InputNumber style={{ width: "100%" }} min={0} placeholder="500000" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="transactionFeePercent" label="Frais (%)">
                <InputNumber style={{ width: "100%" }} min={0} max={100} precision={2} placeholder="1.0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="transactionFeeFixed" label="Frais fixe">
                <InputNumber style={{ width: "100%" }} min={0} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="expirationMinutes" label="Expiration (min)">
                <InputNumber style={{ width: "100%" }} min={1} max={120} placeholder="15" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="config"
            label="Configuration JSON (credentials API)"
            help="JSON contenant les credentials et URLs de l'API du fournisseur"
          >
            <Input.TextArea
              rows={8}
              placeholder={'{\n  "api_url": "https://api.sandbox.orange-sonatel.com",\n  "oauth_url": "https://api.sandbox.orange-sonatel.com/oauth/token",\n  "client_id": "...",\n  "client_secret": "...",\n  "merchant_code": "...",\n  "merchant_number": "..."\n}'}
              style={{ fontFamily: "monospace", fontSize: 12 }}
            />
          </Form.Item>
          <Form.Item
            name="walletCode"
            label="Code du portefeuille"
            extra="Pour un agrégateur : WAVE_SN, ORANGE_SN… Laisser vide pour une intégration directe."
          >
            <Input placeholder="WAVE_SN" />
          </Form.Item>
          <Form.Item
            name="logoUrl"
            label="URL du logo"
            extra="Servie à l'application mobile : changer un logo n'impose pas de publier une version."
          >
            <Input placeholder="https://…/wave.png" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Notes administratives..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Grant Subscription Modal */}
      <Modal
        title="Accorder un abonnement"
        open={grantModalOpen}
        onCancel={() => {
          setGrantModalOpen(false);
          grantForm.resetFields();
        }}
        onOk={handleGrantSubscription}
        okText="Accorder"
        cancelText="Annuler"
        confirmLoading={grantLoading}
        width={480}
      >
        <Form form={grantForm} layout="vertical">
          <Form.Item
            name="userId"
            label="UUID Keycloak de l'utilisateur"
            rules={[{ required: true, message: "UUID Keycloak requis" }]}
            extra="L'identifiant UUID Keycloak de l'utilisateur (visible dans le tableau Utilisateurs)"
          >
            <Input placeholder="ex: a1b2c3d4-e5f6-..." />
          </Form.Item>
          <Form.Item
            name="planId"
            label="Plan"
            rules={[{ required: true, message: "Plan requis" }]}
          >
            <Select
              placeholder="Sélectionner un plan"
              options={(plansData?.data || []).map((p: Plan) => ({
                label: `${p.name} (${p.planId})`,
                value: p.planId,
              }))}
            />
          </Form.Item>
          <Form.Item name="durationDays" label="Durée personnalisée (jours)" extra="Laisser vide pour utiliser la durée du plan">
            <InputNumber style={{ width: "100%" }} min={1} placeholder="Ex: 30" />
          </Form.Item>
          <Form.Item name="note" label="Note interne">
            <Input.TextArea rows={2} placeholder="Ex: Accès offert suite à problème technique" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SubscriptionsPage;
